import { useEffect, useRef, useState } from 'react';
import { defaultGoals } from '../lib/goals';
import {
  curriculumCompletedPositionPct,
  curriculumMilestoneStates,
  curriculumPositionPct,
  curriculumProgressFraction,
  curriculumTierBands,
} from '../lib/curriculumProgress';
import { latestWpmCheckIn } from '../lib/wpmCheckIn';
import type { UserProfile } from '../types';

interface GoalRoadmapProps {
  user: UserProfile;
}

export default function GoalRoadmap({ user }: GoalRoadmapProps) {
  const goals = user.goals ?? defaultGoals();
  const latestCheckIn = latestWpmCheckIn(user);
  const nowWpm = latestCheckIn?.wpm ?? null;
  const nowAcc = latestCheckIn?.accuracy ?? null;
  const baseWpm = user.baselineTranscription?.wpm;
  const baseAcc = user.baselineTranscription?.accuracy;
  const curriculumCompletedPct = curriculumCompletedPositionPct(user);
  const curriculumCurrentPct = curriculumPositionPct(user);
  const curriculumCountPct = curriculumProgressFraction(user) * 100;
  const milestones = curriculumMilestoneStates(user);
  const tierBands = curriculumTierBands();

  return (
    <section className="surface p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="label">Din vej mod målet</h2>
        {user.baselineComposition && (
          <p className="text-xs text-muted">
            Hverdag (udgangspunkt):{' '}
            <span className="font-mono font-medium text-strong">
              {user.baselineComposition.wpm.toFixed(0)} wpm
            </span>
          </p>
        )}
      </div>

      <div className="mt-4 space-y-5">
        <CurriculumTrack
          tierBands={tierBands}
          milestones={milestones}
          completedPct={curriculumCompletedPct}
          currentPct={curriculumCurrentPct}
          trinCountPct={curriculumCountPct}
          targetPct={goals.targetCurriculumProgress * 100}
        />
        <MetricTrack
          label="WPM (hastighedstest)"
          baseline={baseWpm}
          now={nowWpm}
          target={goals.targetWpm}
          format={(v) => `${Math.round(v)}`}
          unit="wpm"
        />
        <MetricTrack
          label="Nøjagtighed"
          baseline={baseAcc !== undefined ? baseAcc * 100 : undefined}
          now={nowAcc !== null ? nowAcc * 100 : null}
          target={goals.targetAccuracy * 100}
          format={(v) => `${Math.round(v)}`}
          unit="%"
        />
      </div>
    </section>
  );
}

function CurriculumTrack({
  tierBands,
  milestones,
  completedPct,
  currentPct,
  trinCountPct,
  targetPct,
}: {
  tierBands: ReturnType<typeof curriculumTierBands>;
  milestones: ReturnType<typeof curriculumMilestoneStates>;
  completedPct: number;
  currentPct: number;
  trinCountPct: number;
  targetPct: number;
}) {
  const completed = clampPct(completedPct);
  const current = clampPct(currentPct);
  const ahead = current > completed + 0.5;

  return (
    <div>
      <div className="flex flex-wrap justify-between gap-x-3 gap-y-1 text-xs text-muted">
        <span>Kurriculum</span>
        <span className="text-right">
          {Math.round(trinCountPct)}% af trin gennemført · mål{' '}
          {Math.round(targetPct)}%
        </span>
      </div>

      <div className="relative mt-2 h-11 overflow-hidden rounded-lg bg-zinc-200 ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-white/5">
        {tierBands.map((band, i) => (
          <div
            key={band.tierId}
            className={`absolute inset-y-0 border-r border-emerald-700/10 dark:border-emerald-400/10 ${
              i % 2 === 0
                ? 'bg-emerald-500/10 dark:bg-emerald-500/15'
                : 'bg-emerald-600/15 dark:bg-emerald-600/20'
            }`}
            style={{
              left: `${clampPct(band.startPct)}%`,
              width: `${clampPct(band.endPct - band.startPct)}%`,
            }}
            title={band.title}
          >
            <span className="pointer-events-none absolute inset-x-0 top-1 truncate px-1 text-center text-[10px] font-medium leading-none text-emerald-800/75 dark:text-emerald-200/70">
              {band.title}
            </span>
          </div>
        ))}

        <div
          className="pointer-events-none absolute inset-y-0 left-0 bg-emerald-600/55 dark:bg-emerald-600/45"
          style={{ width: `${completed}%` }}
          title="Gennemførte trin"
        />

        {ahead && (
          <div
            className="pointer-events-none absolute inset-y-0 bg-emerald-400/30 dark:bg-emerald-400/25"
            style={{
              left: `${completed}%`,
              width: `${current - completed}%`,
            }}
            title="Aktivt trin"
          />
        )}

        <span
          className="absolute top-1/2 z-10 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-amber-500 ring-2 ring-white dark:ring-zinc-900"
          style={{ left: `${current}%` }}
          title="Du er her (aktivt trin)"
        />
      </div>

      <p className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-faint">
        <span>
          <span className="inline-block h-2 w-2 rounded-full bg-emerald-600 align-middle" />{' '}
          Gennemført
        </span>
        {ahead && (
          <span>
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-400/80 align-middle" />{' '}
            Aktivt trin
          </span>
        )}
        <span>
          <span className="inline-block h-1 w-1 rounded-full bg-amber-500 align-middle" />{' '}
          Du er her
        </span>
      </p>

      <div className="relative mt-1 h-7">
        {milestones.map((m) => (
          <div
            key={m.id}
            className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
            style={{ left: `${clampPct(m.positionPct)}%` }}
          >
            <span
              className={`block h-2 w-px rounded-full ${
                m.completed
                  ? 'bg-emerald-600 dark:bg-emerald-400'
                  : m.current
                    ? 'bg-amber-500 dark:bg-amber-400'
                    : 'bg-zinc-300 dark:bg-zinc-600'
              }`}
            />
            <span
              className={`mt-0.5 font-mono text-[10px] leading-none tracking-tight ${
                m.completed
                  ? 'font-semibold text-emerald-800 dark:text-emerald-300'
                  : m.current
                    ? 'font-semibold text-amber-800 dark:text-amber-200'
                    : 'text-faint'
              }`}
              title={m.label}
            >
              {m.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface TrackMarker {
  id: string;
  pct: number;
  className: string;
  title: string;
}

function MetricTrack({
  label,
  baseline,
  now,
  target,
  format,
  unit,
}: {
  label: string;
  baseline?: number;
  now: number | null;
  target: number;
  format: (v: number) => string;
  unit: string;
}) {
  const max = Math.max(target, baseline ?? 0, now ?? 0, 1) * 1.1;
  const barRef = useRef<HTMLDivElement>(null);
  const [barWidthPx, setBarWidthPx] = useState(0);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const update = () => setBarWidthPx(el.getBoundingClientRect().width);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const markers: TrackMarker[] = [];
  if (baseline !== undefined) {
    markers.push({
      id: 'baseline',
      pct: pos(baseline, max),
      className: 'bg-amber-500 ring-2 ring-white dark:ring-zinc-900',
      title: `Udgangspunkt: ${format(baseline)} ${unit}`,
    });
  }
  if (now !== null) {
    markers.push({
      id: 'now',
      pct: pos(now, max),
      className:
        'bg-indigo-600 ring-2 ring-white dark:bg-indigo-400 dark:ring-zinc-900',
      title: `Nu: ${format(now)} ${unit}`,
    });
  }
  markers.push({
    id: 'target',
    pct: pos(target, max),
    className: 'bg-emerald-500 ring-2 ring-white dark:ring-zinc-900',
    title: `Mål: ${format(target)} ${unit}`,
  });

  const offsets = spreadMarkerOffsets(markers, barWidthPx);

  return (
    <div>
      <div className="flex justify-between text-xs text-muted">
        <span>{label}</span>
        <span>
          {now !== null ? format(now) : '—'} / mål {format(target)} {unit}
        </span>
      </div>
      <div ref={barRef} className="relative mt-1.5 h-5 overflow-visible">
        <div className="absolute inset-x-0 top-1/2 h-3 -translate-y-1/2 rounded-full bg-zinc-200 dark:bg-zinc-800">
          {now !== null && (
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-indigo-400/80 dark:bg-indigo-500/60"
              style={{ width: `${pos(now, max)}%` }}
            />
          )}
        </div>
        {markers.map((m, i) => (
          <BarMarker
            key={m.id}
            pct={m.pct}
            offsetY={offsets[i]!}
            className={m.className}
            title={m.title}
          />
        ))}
      </div>
    </div>
  );
}

/** Lodret forskydning kun når prik-centre er tættere end prikkens bredde. */
const MARKER_DOT_PX = 10; // h-2.5 w-2.5

function spreadMarkerOffsets(
  markers: TrackMarker[],
  barWidthPx: number,
): number[] {
  const offsets = new Array<number>(markers.length).fill(0);
  if (markers.length <= 1 || barWidthPx <= 0) return offsets;

  const order = markers
    .map((m, i) => ({ i, x: (m.pct / 100) * barWidthPx }))
    .sort((a, b) => a.x - b.x);

  const slotPx = [-8, 0, 8];
  let group: Array<{ i: number; x: number }> = [];

  const flush = () => {
    if (group.length <= 1) {
      if (group.length === 1) offsets[group[0]!.i] = 0;
    } else if (group.length === 2) {
      offsets[group[0]!.i] = -7;
      offsets[group[1]!.i] = 7;
    } else {
      group.forEach((g, j) => {
        offsets[g.i] = slotPx[j] ?? (j - (group.length - 1) / 2) * 8;
      });
    }
    group = [];
  };

  for (const item of order) {
    const prev = group[group.length - 1];
    const touches =
      prev !== undefined && item.x - prev.x <= MARKER_DOT_PX;

    if (!prev || touches) {
      group.push(item);
    } else {
      flush();
      group.push(item);
    }
  }
  flush();

  return offsets;
}

function BarMarker({
  pct,
  className,
  title,
  offsetY = 0,
}: {
  pct: number;
  className: string;
  title: string;
  offsetY?: number;
}) {
  return (
    <span
      className={`absolute z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${className}`}
      style={{
        left: `${clampPct(pct)}%`,
        top: `calc(50% + ${offsetY}px)`,
      }}
      title={title}
    />
  );
}

function pos(value: number, max: number): number {
  return clampPct((value / max) * 100);
}

function clampPct(n: number): number {
  return Math.min(100, Math.max(0, n));
}
